import { crearTabla, mostrarToast } from '@plataforma/ui';

import { guardarPlan, listarPlanesBackoffice } from '../api/backoffice.js';
import { exigirSuperAdmin } from '../guardas.js';

import { estadoCarga, estadoError, moneda, normalizarCaracteristicas } from './backoffice-ui.js';
import { montarShell } from './shell.js';

export async function vistaPlanes(contenedorRaiz) {
  if (!exigirSuperAdmin()) return;

  const contenido = montarShell(contenedorRaiz, '/planes');
  contenido.innerHTML = `
    <div class="pf-backoffice-header">
      <div>
        <h1>Planes</h1>
        <p class="pf-texto-suave">Catálogo comercial, límites y disponibilidad.</p>
      </div>
      <button type="button" class="pf-boton pf-boton--primario" id="pf-btn-nuevo-plan">Nuevo plan</button>
    </div>
    <div id="pf-planes-tabla">${estadoCarga()}</div>
    <pf-modal titulo="Plan" id="pf-modal-plan"></pf-modal>
  `;

  contenido.querySelector('#pf-btn-nuevo-plan').addEventListener('click', () => {
    abrirFormularioPlan(contenido, null);
  });

  await cargarPlanes(contenido);
}

async function cargarPlanes(contenido) {
  const destino = contenido.querySelector('#pf-planes-tabla');
  destino.innerHTML = estadoCarga();

  const { data, error } = await listarPlanesBackoffice();
  if (error) {
    destino.innerHTML = estadoError('No se pudieron cargar los planes.');
    return;
  }

  const tabla = crearTabla({
    filaVacia: 'No hay planes configurados.',
    columnas: [
      { clave: 'nombre', titulo: 'Plan' },
      { titulo: 'Precio', render: (fila) => moneda(fila.precio_mensual) },
      { clave: 'estado', titulo: 'Estado' },
      { clave: 'limite_productos', titulo: 'Productos' },
      { clave: 'limite_imagenes', titulo: 'Imágenes' },
      { clave: 'limite_usuarios_admin', titulo: 'Admins' },
      { clave: 'empresas_usando', titulo: 'Empresas' },
      {
        titulo: 'Acciones',
        render: (fila) => {
          const contenedor = document.createElement('div');
          contenedor.className = 'pf-backoffice-actions';

          const editar = document.createElement('button');
          editar.type = 'button';
          editar.className = 'pf-boton pf-boton--secundario pf-boton--chico';
          editar.textContent = 'Editar';
          editar.addEventListener('click', () => abrirFormularioPlan(contenido, fila));
          contenedor.appendChild(editar);

          const toggle = document.createElement('button');
          toggle.type = 'button';
          toggle.className = 'pf-boton pf-boton--secundario pf-boton--chico';
          toggle.textContent = fila.estado === 'activo' ? 'Desactivar' : 'Activar';
          toggle.addEventListener('click', () =>
            abrirFormularioPlan(contenido, {
              ...fila,
              estado: fila.estado === 'activo' ? 'inactivo' : 'activo',
              motivoSugerido:
                fila.estado === 'activo'
                  ? 'Desactivación administrativa del plan. No se elimina historial.'
                  : 'Reactivación administrativa del plan.',
            }),
          );
          contenedor.appendChild(toggle);

          return contenedor;
        },
      },
    ],
    filas: data ?? [],
  });

  destino.innerHTML = '';
  destino.appendChild(tabla);
}

function abrirFormularioPlan(contenido, plan) {
  const modal = contenido.querySelector('#pf-modal-plan');
  const caracteristicas = JSON.stringify(plan?.caracteristicas ?? {}, null, 2);

  modal.innerHTML = `
    <form id="pf-form-plan">
      <div class="pf-backoffice-detail">
        <div class="pf-campo">
          <label for="pf-plan-nombre">Nombre</label>
          <input id="pf-plan-nombre" value="${plan?.nombre ?? ''}" required />
        </div>
        <div class="pf-campo">
          <label for="pf-plan-precio">Precio mensual</label>
          <input id="pf-plan-precio" type="number" min="0" step="0.01" value="${plan?.precio_mensual ?? 0}" required />
        </div>
        <div class="pf-campo">
          <label for="pf-plan-estado">Estado</label>
          <select id="pf-plan-estado">
            <option value="activo" ${plan?.estado !== 'inactivo' ? 'selected' : ''}>Activo</option>
            <option value="inactivo" ${plan?.estado === 'inactivo' ? 'selected' : ''}>Inactivo</option>
          </select>
        </div>
        <div class="pf-campo">
          <label for="pf-plan-admins">Usuarios admin</label>
          <input id="pf-plan-admins" type="number" min="1" value="${plan?.limite_usuarios_admin ?? 1}" required />
        </div>
        <div class="pf-campo">
          <label for="pf-plan-productos">Productos</label>
          <input id="pf-plan-productos" type="number" min="0" value="${plan?.limite_productos ?? 50}" required />
        </div>
        <div class="pf-campo">
          <label for="pf-plan-imagenes">Imágenes por producto</label>
          <input id="pf-plan-imagenes" type="number" min="0" value="${plan?.limite_imagenes ?? 3}" required />
        </div>
      </div>
      <div class="pf-campo">
        <label for="pf-plan-caracteristicas">Características JSON o una por línea</label>
        <textarea id="pf-plan-caracteristicas" rows="6">${caracteristicas}</textarea>
      </div>
      <div class="pf-campo">
        <label for="pf-plan-motivo">Motivo</label>
        <textarea id="pf-plan-motivo" rows="3" required>${plan?.motivoSugerido ?? ''}</textarea>
      </div>
      <p class="pf-campo__error" id="pf-plan-error" hidden></p>
      <div class="pf-formulario__footer">
        <button type="button" class="pf-boton pf-boton--secundario" id="pf-plan-cancelar">Cancelar</button>
        <button type="submit" class="pf-boton pf-boton--primario">Guardar</button>
      </div>
    </form>
  `;

  modal.querySelector('#pf-plan-cancelar').addEventListener('click', () => modal.cerrar());
  modal.querySelector('#pf-form-plan').addEventListener('submit', async (evento) => {
    evento.preventDefault();
    const errorEl = modal.querySelector('#pf-plan-error');
    errorEl.hidden = true;

    const datos = {
      id: plan?.id ?? null,
      nombre: modal.querySelector('#pf-plan-nombre').value.trim(),
      precio_mensual: modal.querySelector('#pf-plan-precio').value,
      estado: modal.querySelector('#pf-plan-estado').value,
      limite_productos: modal.querySelector('#pf-plan-productos').value,
      limite_imagenes: modal.querySelector('#pf-plan-imagenes').value,
      limite_usuarios_admin: modal.querySelector('#pf-plan-admins').value,
      caracteristicas: normalizarCaracteristicas(
        modal.querySelector('#pf-plan-caracteristicas').value,
      ),
      motivo: modal.querySelector('#pf-plan-motivo').value.trim(),
    };

    const { error } = await guardarPlan(datos);
    if (error) {
      errorEl.textContent = error.message;
      errorEl.hidden = false;
      return;
    }

    mostrarToast('Plan guardado y auditado.', { tono: 'exito' });
    modal.cerrar();
    await cargarPlanes(contenido);
  });

  modal.abrir();
}
